import {retrieveDealData} from 'Src/actions/DealActions'
import {pushToCrm} from 'Src/apis/leads'
import {CTA_TYPES, SOFT_PULL_STATUS} from 'Src/constants'
import {trans} from 'Src/utilities/Helpers'
import useConfig from 'Src/utilities/useConfig'

export const getAndSetCityList = async (api, zipCode, registrationDispatch, registrationState) => {
  if (registrationState?.cityListZipCode === zipCode) {
    return registrationState.cityList
  }

  if (zipCode.length !== 5) {
    registrationDispatch({type: 'reset'})
    registrationDispatch({
      type: 'setCityListLoadingError',
      payload: trans('validation.invalid', {attribute: trans('zip_code')}),
    })
    registrationDispatch({type: 'setCityListLoading', payload: false})

    return false
  }

  let response = false

  try {
    response = await api.get(`/cities/${zipCode}`)
  } catch (exception) {
    console.error(exception)
  }

  const cityList = response?.data?.data ?? []

  if (!cityList?.length) {
    registrationDispatch({
      type: 'reset',
    })
    registrationDispatch({
      type: 'setCityListLoadingError',
      payload: trans('city_not_found_zip', {zip: zipCode}),
    })

    return false
  }

  registrationDispatch({type: 'setCityList', payload: cityList})
  registrationDispatch({type: 'setCityListZipCode', payload: zipCode})
  registrationDispatch({type: 'setCityListLoading', payload: false})
  registrationDispatch({type: 'setCityListLoadingError', payload: false})

  return cityList
}

/**
 *
 * @param {Object} options
 * @param {number|null} options.customerId
 * @param {string} options.navigationPath
 * @param {function} options.navDispatch
 * @param {History<unknown>} options.history
 * @param {function} [options.optionalFn]
 * @param {function} [options.bypassRegistrationFn]
 * @param {boolean} [options.runOptionalIfRegistered]
 * @param {boolean} [options.skipAuthCheck]
 * @returns undefined
 */
export const navigateIfUserRegistered = ({
  customerId,
  navigationPath,
  navDispatch,
  history,
  optionalFn = null,
  bypassRegistrationFn = null,
  runOptionalIfRegistered = true,
  skipAuthCheck = false,
}) => {
  if (skipAuthCheck || customerId) {
    if (optionalFn && runOptionalIfRegistered) {
      optionalFn()
    }

    if (bypassRegistrationFn) {
      bypassRegistrationFn()
    }

    if (navigationPath) {
      history.push(navigationPath)
    }

    return
  }

  navDispatch({
    type: 'navigateAfterRegistration',
    payload: {page: navigationPath},
  })

  history.push('/register')
}

export const getVehicleTypes = async (a2zApi) => {
  let vehicleTypesResponse

  try {
    vehicleTypesResponse = await a2zApi.get('/vehicles/types')
  } catch (error) {
    console.error('Could not fetch Vehicle Types.', error)
  }

  return vehicleTypesResponse?.data?.data || []
}

export const getVehicleTypeByName = async (a2zApi, vehicleTypeName) => {
  const vehicleTypes = await getVehicleTypes(a2zApi)

  return vehicleTypes.find((type) => type.attributes.name === vehicleTypeName.toLowerCase())
}

export const createLeadVehicle = async (
  leadId,
  vehicle,
  dealershipId,
  customerDispatch,
  a2zApi,
  a2zCustomerApi,
  isPriceLocked,
  customerLeadVehicles = null,
  paymentCalculations = null
) => {
  if (!leadId || !vehicle) {
    return
  }

  const vehicleTypeId = (await getVehicleTypeByName(a2zApi, vehicle.type))?.id

  if (!customerLeadVehicles) {
    const leadResponse = await a2zCustomerApi.get(
      `/customers/leads/${leadId}?include=leadVehicles,leadVehicles.vehicle,leadVehicles.paymentCalculation`
    )
    const includedLeadData = leadResponse.data.included
    const existingVehicles = includedLeadData.filter(({type}) => type === 'vehicles')
    const leadVehicles = includedLeadData.filter(({type}) => type === 'leadVehicles')

    paymentCalculations = includedLeadData.filter(({type}) => type === 'paymentCalculations')

    customerLeadVehicles = leadVehicles.map((leadVehicle) => {
      const existingVehicle = existingVehicles.find(
        ({id}) => id === leadVehicle.attributes?.vehicle_id?.toString()
      )
      leadVehicle.attributes.vehicle = {id: existingVehicle, ...existingVehicle.attributes}

      return leadVehicle
    })
  }

  const existingLeadVehicle = customerLeadVehicles?.find(
    (leadVehicle) => leadVehicle?.attributes?.vehicle?.vin === vehicle.vin
  )

  if (existingLeadVehicle) {
    await pushToCrm(a2zCustomerApi, existingLeadVehicle.id)
    existingLeadVehicle.isCurrent = true

    const paymentCalculation = paymentCalculations?.find(
      (calculation) =>
        existingLeadVehicle?.attributes?.payment_calculation_id?.toString() ===
        calculation.id.toString()
    )
    existingLeadVehicle.paymentCalculation = paymentCalculation

    customerDispatch({type: 'setLeadVehicles', payload: customerLeadVehicles})

    if (existingLeadVehicle.attributes.vehicle.type === 'new' && !isPriceLocked) {
      customerDispatch({type: 'setExistingVehicle', payload: existingLeadVehicle})
    }

    if (
      existingLeadVehicle.attributes.vehicle.type === 'used' ||
      !existingLeadVehicle.attributes.vehicle.certified
    ) {
      customerDispatch({type: 'setExistingVehicle', payload: existingLeadVehicle})
    }

    return
  }

  const leadVehicleData = {
    data: {
      type: 'leadVehicles',
      attributes: {},
      relationships: {
        lead: {
          data: {
            type: 'leads',
            id: leadId,
          },
        },
        dealership: {
          data: {
            type: 'dealerships',
            id: dealershipId,
          },
        },
        vehicle: {
          data: {type: 'vehicles'},
        },
      },
    },
    included: [
      {
        type: 'vehicles',
        attributes: vehicle,
        relationships: {
          vehicleType: {
            data: {
              type: 'vehicleTypes',
              id: vehicleTypeId,
            },
          },
        },
      },
    ],
  }

  const leadVehicleCreateResponseData = await a2zCustomerApi
    .post(`/customers/leads/${leadId}/vehicles`, leadVehicleData)
    .then((response) => response.data)

  const newLeadVehicle = leadVehicleCreateResponseData?.data

  if (!newLeadVehicle?.id) {
    return
  }

  await pushToCrm(a2zCustomerApi, newLeadVehicle.id)
  newLeadVehicle.isCurrent = true
  customerLeadVehicles.push(newLeadVehicle)
  customerDispatch({type: 'setLeadVehicles', payload: customerLeadVehicles})
}

/**
 * Asynchronously creates a soft pull credit report if the user has clicked the "Soft Pull" CTA and filled out the "Get Pre-Qualified" form.
 *
 * @param {Object} options.api
 * @param {Object} options.customerState
 * @param {Object} options.customerData
 * @returns false | {score: string, dealershipId: string, submittedAt: string}
 */
const createSoftPullCreditReport = async ({api, customerState, customerData}) => {
  const config = useConfig()
  // User has clicked the "Soft Pull" CTA and has filled out the "Get Pre-Qualified" Form.
  const isSoftPullExperience = config.ctaType === CTA_TYPES.SOFT_PULL && customerState?.softPullData
  const isCustomerLoggedIn = !!customerData?.customerData?.id
  const shouldCreateSoftPullCreditReport = isSoftPullExperience && isCustomerLoggedIn
  let response

  if (!shouldCreateSoftPullCreditReport) {
    return SOFT_PULL_STATUS.UNAVAILABLE
  }

  const payload = {
    data: {
      attributes: {
        ...customerState.softPullData,
        customerId: customerData?.customerData?.id,
      },
    },
  }

  try {
    // Create Soft Pull Credit Report
    response = await api.post('/digital-retail/soft-pull', payload)
  } catch (exception) {
    console.error(exception)
  }

  const responseDataAttributes = response?.data?.data?.attributes

  if (responseDataAttributes) {
    const {creditScore: score, timeStamp: submittedAt} = responseDataAttributes

    return {
      score,
      dealershipId: config.dealership.id,
      submittedAt,
    }
  }

  return SOFT_PULL_STATUS.UNAVAILABLE
}

/**
 * Asynchronously retrieves and processes comprehensive data related to a lead, including trade, deal, soft pull credit report, and vehicle info
 *
 * @param {string} leadId
 * @param {Object} vehicle
 * @param {string} dealershipId
 * @param {Object} customerData
 * @param {Function} customerDispatch
 * @param {Function} dealDispatch
 * @param {Object} dealState
 * @param {Object} api
 * @param {Object} customerApi
 * @param {boolean} isPriceLocked
 * @param {boolean} useCache
 * @param {boolean} isSaveDealEnabled
 * @param {Object} customerState
 *
 * @returns {Promise<Object>}
 */
export const getLeadTradeAndDealData = async (
  leadId,
  vehicle,
  dealershipId,
  customerData,
  customerDispatch,
  dealDispatch,
  dealState,
  api,
  customerApi,
  isPriceLocked,
  useCache,
  isSaveDealEnabled,
  customerState
) => {
  const config = useConfig()

  const leadResponseData = await customerApi
    .get(
      `/customers/leads/${leadId}?include=leadVehicles,leadVehicles.vehicle,leadVehicles.paymentCalculation,trades,trades.vehicle,validSoftPullCreditReport`
    )
    .then((response) => response.data)

  if (leadResponseData.data[0].attributes?.hasActiveCreditApp) {
    customerDispatch({type: 'setHasActiveCreditApp', payload: true})
  }

  const leadVehicles = leadResponseData.included.filter(({type}) => type === 'leadVehicles')
  const vehicles = leadResponseData.included.filter(({type}) => type === 'vehicles')
  const vehiclesById = vehicles.reduce(
    (previousValue, vehicleRecord) => ({...previousValue, [vehicleRecord.id]: vehicleRecord}),
    {}
  )

  const paymentCalculations = leadResponseData.included.filter(
    ({type}) => type === 'paymentCalculations'
  )

  const existingSoftPullCreditReport = leadResponseData.included?.find(
    ({type}) => type === 'soft-pull-credit-reports'
  )?.attributes

  const softPullCreditReport =
    existingSoftPullCreditReport ||
    (await createSoftPullCreditReport({api, customerState, customerData}))

  await customerDispatch({
    type: 'setCustomerData',
    payload: {
      softPullCreditReport,
    },
  })

  const existingLeadVehicles = leadVehicles.map((leadVehicle) => {
    const existingVehicle = vehiclesById[leadVehicle.attributes.vehicle_id]

    leadVehicle.attributes.vehicle = {id: existingVehicle.id, ...existingVehicle.attributes}
    delete leadVehicle.relationships

    return leadVehicle
  })

  if (isSaveDealEnabled) {
    const previouslySavedLeadVehicle = getPreviouslySavedLeadVehicleQuote(
      vehicle.vin,
      leadVehicles,
      paymentCalculations
    )

    await customerDispatch({type: 'setPreviouslySavedQuote', payload: previouslySavedLeadVehicle})
    await customerDispatch({
      type: 'setShowPreviousDealModal',
      payload: ![CTA_TYPES.SOFT_PULL, CTA_TYPES.TRADE].includes(config.ctaType),
    })
  }

  let tradePayload = null
  const trade = leadResponseData.included
    .filter(({type}) => type === 'trades') // get all the trades
    .sort((a, b) => new Date(b.attributes.updated_at) - new Date(a.attributes.updated_at))[0] // Sort by updated_at in descending order, getting the most recent trade

  if (trade) {
    const tradeVehicle = leadResponseData.included.find(
      ({type, id}) => type === 'vehicles' && id === trade.attributes?.vehicle_id?.toString()
    )
    tradePayload = {
      id: trade.id,
      ...trade.attributes,
      vehicle: {id: tradeVehicle?.id, ...tradeVehicle?.attributes},
    }

    customerDispatch({type: 'setTrade', payload: tradePayload})
  }

  customerDispatch({type: 'setLeadVehicles', payload: existingLeadVehicles})

  await createLeadVehicle(
    leadId,
    vehicle,
    dealershipId,
    customerDispatch,
    api,
    customerApi,
    isPriceLocked,
    existingLeadVehicles,
    paymentCalculations
  )

  if (vehicle?.id) {
    // Only use soft pull credit score for calculations if:
    // 1. there is a valid soft pull credit score
    // 2. the user clicked on "Get Pre-Approved" CTA
    const creditScoreData =
      config.ctaType === CTA_TYPES.SOFT_PULL && softPullCreditReport
        ? {
            creditScore: softPullCreditReport?.score?.toString(),
            hasSoftPullCreditScore: true,
          }
        : {}

    await retrieveDealData(
      {vin: vehicle.vin, dealershipId, isPriceLocked, useCache},
      dealDispatch,
      api,
      {
        ...dealState,
        ...creditScoreData,
      },
      {...customerData, trade: tradePayload}
    )
  }

  return {leadVehicles: existingLeadVehicles}
}

const createCalculationMap = (calculations) => {
  return new Map(calculations.map((calculation) => [calculation.id, calculation.attributes]))
}

const getVehicleCalculations = (paymentCalculations, vehicleVin) => {
  const calculations = paymentCalculations.filter((calculation) => {
    const {
      attributes: {
        deal_presenter: {vin: calculationVin},
      },
    } = calculation

    return calculationVin === vehicleVin
  })

  return createCalculationMap(calculations)
}

const findMostRecentVehicleQuote = (leadVehicles, vehicleVin, calculationsMap) => {
  return leadVehicles.reduce((mostRecent, current) => {
    const {attributes: leadVehicleData} = current
    const leadCalculationId = leadVehicleData.payment_calculation_id?.toString()
    const leadVehicleVin = leadVehicleData.vehicle?.vin

    if (!leadCalculationId || vehicleVin !== leadVehicleVin) {
      return mostRecent
    }

    if (
      (mostRecent && new Date(leadVehicleData.created_at) < new Date(mostRecent.created_at)) ||
      !calculationsMap.has(leadCalculationId)
    ) {
      return mostRecent
    }

    current.calculation = calculationsMap.get(leadCalculationId)

    return current
  }, null)
}

const getPreviouslySavedLeadVehicleQuote = (vehicleVin, leadVehicles, paymentCalculations) => {
  const calculationsMap = getVehicleCalculations(paymentCalculations, vehicleVin)

  return findMostRecentVehicleQuote(leadVehicles, vehicleVin, calculationsMap)
}
